import { useEffect, useState } from "react";
import { Copy, Download, FileSignature, Loader2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, downloadFile } from "@/lib/api";
import { toast } from "sonner";

const TYPE = { editor: "Grant editor", cpa: "CPA", attorney: "Attorney" };
const STATUS = { pending: "bg-amber-50 text-amber-700 border-amber-200", approved: "bg-emerald-50 text-emerald-700 border-emerald-200", declined: "bg-slate-100 text-slate-500 border-slate-200", approving: "bg-amber-50 text-amber-700 border-amber-200" };

export function ProAgreementCard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/pro/agreement").then((r) => setD(r.data)).catch(() => setD({ has_agreement: false })); }, []);
  if (!d?.has_agreement) return null;
  const get = async () => {
    setBusy(true);
    try { await downloadFile("/pro/agreement/pdf", "GrantVerify_Partner_Agreement.pdf"); } catch { toast.error("Couldn't download agreement"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 flex flex-wrap items-center gap-4" data-testid="pro-agreement-card">
      <FileSignature className="h-6 w-6 text-emerald-600" />
      <div className="flex-1 min-w-0"><div className="font-semibold text-slate-900">Signed partner agreement</div><div className="text-xs text-slate-500" data-testid="pro-agreement-summary">${d.rate} flat fee per completed job · signed {new Date(d.signed_at).toLocaleDateString()}</div></div>
      <Button variant="outline" onClick={get} disabled={busy} data-testid="pro-agreement-download-button">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Download PDF</Button>
    </div>
  );
}

export function ProReferrals() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/pro/referrals").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const copy = () => { navigator.clipboard.writeText(d.link); toast.success("Invite link copied"); };
  return (
    <div className="bg-slate-900 text-white rounded-lg p-6" data-testid="pro-referrals">
      <div className="flex items-center gap-2"><Users className="h-5 w-5 text-emerald-400" /><h3 className="font-display text-xl font-bold">Invite colleagues</h3></div>
      <p className="text-sm text-slate-300 mt-1">Know a certified grant editor, CPA or attorney? Share your personal link — we'll track everyone you bring in.</p>
      <div className="mt-4 flex gap-2">
        <code className="flex-1 min-w-0 truncate bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-emerald-300" data-testid="pro-referral-link">{d.link}</code>
        <Button onClick={copy} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950" data-testid="pro-referral-copy-button"><Copy className="h-4 w-4 mr-1" />Copy</Button>
      </div>
      <div className="mt-5 text-sm" data-testid="pro-referral-stats"><b className="text-white">{d.referrals.length}</b> <span className="text-slate-400">invited ·</span> <b className="text-emerald-400">{d.approved}</b> <span className="text-slate-400">approved</span></div>
      {d.referrals.length > 0 && (
        <div className="mt-3 divide-y divide-slate-800 border-t border-slate-800" data-testid="pro-referral-list">
          {d.referrals.map((r, i) => (
            <div key={i} className="py-2 flex items-center gap-3 text-sm">
              <span className="flex-1 truncate">{r.name} <span className="text-slate-500 text-xs">· {TYPE[r.pro_type]} · {r.state}</span></span>
              <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${STATUS[r.status]}`}>{r.status === "approving" ? "pending" : r.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
